import {
  StationStatus,
  SessionStatus,
  TransactionStatus,
  UserRole,
  UserStatus,
} from '../shared/enums/status';
import { ConnectorTypeEnum } from '../shared/enums/connector-type';
import type { FilterOption } from '../types/filterOptionTypes';
import type { FilterChipOption } from '../types/mobileFilterSheetTypes';

// Labels are i18n keys; consumers resolve them with t(option.label).
const ALL_DOT = 'status-option-dot-all';

export const STATION_STATUS_OPTIONS: readonly FilterOption<StationStatus>[] = [
  { value: '', label: 'dashboard.filters.allStatuses', dotClass: ALL_DOT },
  { value: StationStatus.ACTIVE, label: 'dashboard.filters.stationStatus.active', dotClass: 'status-option-dot-active' },
  { value: StationStatus.IN_USE, label: 'dashboard.filters.stationStatus.inUse', dotClass: 'status-option-dot-in-use' },
  { value: StationStatus.INACTIVE, label: 'dashboard.filters.stationStatus.inactive', dotClass: 'status-option-dot-inactive' },
] as const;

export const SESSION_STATUS_OPTIONS: readonly FilterOption<SessionStatus>[] = [
  { value: '', label: 'dashboard.filters.allStatuses', dotClass: ALL_DOT },
  { value: SessionStatus.CHARGING, label: 'dashboard.filters.sessionStatus.charging', dotClass: 'status-option-dot-in-use' },
  { value: SessionStatus.FINISHED, label: 'dashboard.filters.sessionStatus.finished', dotClass: 'status-option-dot-active' },
  { value: SessionStatus.PREPARE, label: 'dashboard.filters.sessionStatus.preparing', dotClass: 'status-option-dot-inactive' },
  // Start attempts the charger never turned into a transaction. Worth
  // filtering for: a run of them on one connector means the bay is refusing
  // starts, not that drivers changed their minds.
  { value: SessionStatus.CANCELLED, label: 'dashboard.filters.sessionStatus.cancelled', dotClass: 'status-option-dot-failure' },
  // Charges nobody ordered, stopped by the backend: the energy is on record
  // but billed to no one.
  { value: SessionStatus.UNAUTHORIZED, label: 'dashboard.filters.sessionStatus.unauthorized', dotClass: 'status-option-dot-failure' },
] as const;

export const TRANSACTION_STATUS_OPTIONS: readonly FilterOption<TransactionStatus>[] = [
  { value: '', label: 'dashboard.filters.allStatuses', dotClass: ALL_DOT },
  { value: TransactionStatus.SUCCESS, label: 'dashboard.filters.transactionStatus.success', dotClass: 'status-option-dot-active' },
  { value: TransactionStatus.PROCESSING, label: 'dashboard.filters.transactionStatus.processing', dotClass: 'status-option-dot-in-use' },
  { value: TransactionStatus.CREATED, label: 'dashboard.filters.transactionStatus.created', dotClass: 'status-option-dot-inactive' },
  { value: TransactionStatus.FAILURE, label: 'dashboard.filters.transactionStatus.failed', dotClass: 'status-option-dot-failure' },
  { value: TransactionStatus.REVERSED, label: 'dashboard.filters.transactionStatus.reversed', dotClass: 'status-option-dot-reversed' },
] as const;

export const USER_ROLE_OPTIONS: readonly FilterOption<UserRole>[] = [
  { value: '', label: 'dashboard.filters.allRoles', dotClass: ALL_DOT },
  { value: UserRole.ADMIN, label: 'dashboard.filters.userRole.admin', dotClass: 'status-option-dot-admin' },
  { value: UserRole.MANAGER, label: 'dashboard.filters.userRole.manager', dotClass: 'status-option-dot-manager' },
  { value: UserRole.USER, label: 'dashboard.filters.userRole.user', dotClass: 'status-option-dot-user' },
] as const;

export const USER_STATUS_OPTIONS: readonly FilterOption<UserStatus>[] = [
  { value: '', label: 'dashboard.filters.allStatuses', dotClass: ALL_DOT },
  { value: UserStatus.ACTIVE, label: 'dashboard.filters.userStatus.active', dotClass: 'status-option-dot-active' },
  { value: UserStatus.INACTIVE, label: 'dashboard.filters.userStatus.inactive', dotClass: 'status-option-dot-inactive' },
  { value: UserStatus.BLOCKED, label: 'dashboard.filters.userStatus.blocked', dotClass: 'status-option-dot-failure' },
] as const;

export const CONNECTOR_TYPE_OPTIONS: readonly FilterOption<ConnectorTypeEnum>[] = [
  { value: '', label: 'dashboard.filters.allConnectors', dotClass: ALL_DOT },
  ...Object.values(ConnectorTypeEnum).map((v) => ({
    value: v,
    label: v,
    dotClass: 'status-option-dot-active',
  })),
] as const;

export const DATE_RANGE_PRESETS = [
  'Today',
  'Yesterday',
  'Last 7 days',
  'Last 30 days',
  'This Month',
] as const;

export type DateRangePreset = (typeof DATE_RANGE_PRESETS)[number];

// ---------- Chip options (derived — enum-backed, no "All" sentinel, no dotClass) ----------

const toChipOptions = <T extends string>(
  options: readonly FilterOption<T>[],
): readonly FilterChipOption<T>[] =>
  options
    .filter((opt): opt is FilterOption<T> & { value: T } => opt.value !== '')
    .map(({ value, label }) => ({ value, label }));

export const STATION_STATUS_CHIPS = toChipOptions(STATION_STATUS_OPTIONS);
export const SESSION_STATUS_CHIPS = toChipOptions(SESSION_STATUS_OPTIONS);
export const TRANSACTION_STATUS_CHIPS = toChipOptions(TRANSACTION_STATUS_OPTIONS);
export const USER_ROLE_CHIPS = toChipOptions(USER_ROLE_OPTIONS);
export const USER_STATUS_CHIPS = toChipOptions(USER_STATUS_OPTIONS);
export const CONNECTOR_TYPE_CHIPS = toChipOptions(CONNECTOR_TYPE_OPTIONS);

// Date range chip labels are i18n keys; resolved by callers.
const DATE_RANGE_LABEL_KEY: Record<DateRangePreset, string> = {
  Today: 'dashboard.filters.dateRange.today',
  Yesterday: 'dashboard.filters.dateRange.yesterday',
  'Last 7 days': 'dashboard.filters.dateRange.last7days',
  'Last 30 days': 'dashboard.filters.dateRange.last30days',
  'This Month': 'dashboard.filters.dateRange.thisMonth',
};

export const DATE_RANGE_CHIPS: readonly FilterChipOption<DateRangePreset>[] =
  DATE_RANGE_PRESETS.map((preset) => ({ value: preset, label: DATE_RANGE_LABEL_KEY[preset] }));
